import React, { useState, useEffect } from "react";

const ROICalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState(100000);
  const [finalValue, setFinalValue] = useState(150000);
  const [additionalInvestment, setAdditionalInvestment] = useState(0);
  const [investmentPeriod, setInvestmentPeriod] = useState(3);
  const [periodType, setPeriodType] = useState("years");
  const [dividendsReceived, setDividendsReceived] = useState(5000);
  const [result, setResult] = useState({
    totalInvestment: "0.00",
    totalReturn: "0.00",
    netProfit: "0.00",
    roiPercentage: "0.00",
    annualizedReturn: "0.00",
    monthlyReturn: "0.00",
    totalGain: "0.00",
    investmentCategory: "",
  });

  // ROI Calculation Function
  const calculateROI = () => {
    const initial = parseFloat(initialInvestment);
    const final = parseFloat(finalValue);
    const additional = parseFloat(additionalInvestment);
    const dividends = parseFloat(dividendsReceived);
    let period = parseFloat(investmentPeriod);

    if (isNaN(initial) || isNaN(final) || initial <= 0 || final < 0 || period <= 0) return;

    // Convert period to years for annualized calculation
    const years = periodType === "years" ? period : period / 12;
    const months = periodType === "years" ? period * 12 : period;

    const totalInvestment = initial + additional;
    const totalReturn = final + dividends;
    const netProfit = totalReturn - totalInvestment;
    const roiPercentage = (netProfit / totalInvestment) * 100;
    
    // Calculate annualized return (CAGR)
    const annualizedReturn = years > 0 ? (Math.pow(totalReturn / totalInvestment, 1 / years) - 1) * 100 : 0;
    
    // Calculate monthly return
    const monthlyReturn = months > 0 ? (netProfit / months) : 0;
    
    const totalGain = netProfit;

    let investmentCategory = "";
    if (roiPercentage < 0) {
      investmentCategory = "Loss";
    } else if (roiPercentage <= 5) {
      investmentCategory = "Below Average";
    } else if (roiPercentage <= 12) {
      investmentCategory = "Average";
    } else if (roiPercentage <= 20) {
      investmentCategory = "Good";
    } else if (roiPercentage <= 30) {
      investmentCategory = "Very Good";
    } else {
      investmentCategory = "Excellent";
    }

    setResult({
      totalInvestment: totalInvestment.toFixed(2),
      totalReturn: totalReturn.toFixed(2),
      netProfit: netProfit.toFixed(2),
      roiPercentage: roiPercentage.toFixed(2),
      annualizedReturn: annualizedReturn.toFixed(2),
      monthlyReturn: monthlyReturn.toFixed(2),
      totalGain: totalGain.toFixed(2),
      investmentCategory,
    });
  };

  useEffect(() => {
    calculateROI();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <h1>ROI Calculator</h1>
          <p>
            Calculate your Return on Investment and analyze the performance of your investments
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Investment Details
              </h2>

              {/* Initial Investment */}
              <div className="input-group">
                <label>Initial Investment</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={initialInvestment}
                    onChange={(e) => setInitialInvestment(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 500000, 1000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setInitialInvestment(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Final Value */}
              <div className="input-group">
                <label>Current/Final Value</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={finalValue}
                    onChange={(e) => setFinalValue(e.target.value)}
                    className="calc-input"
                    placeholder="150000"
                  />
                </div>
                <div className="quick-select-group">
                  {[75000, 150000, 600000, 1200000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setFinalValue(amount)}
                      className="quick-select-btn secondary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Investment */}
              <div className="input-group">
                <label>Additional Investments</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={additionalInvestment}
                    onChange={(e) => setAdditionalInvestment(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 25000, 50000, 100000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setAdditionalInvestment(amount)}
                      className="quick-select-btn success"
                    >
                      {amount === 0 ? "₹0" : amount >= 100000 ? `₹${(amount / 100000).toFixed(0)}L` : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Investment Period */}
              <div className="input-group">
                <label>Investment Period</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={investmentPeriod}
                    onChange={(e) => setInvestmentPeriod(e.target.value)}
                    className="calc-input"
                    placeholder="3"
                  />
                </div>
                <div className="quick-select-group">
                  {periodType === "years" 
                    ? [1, 3, 5, 10].map((years) => (
                        <button
                          key={years}
                          onClick={() => setInvestmentPeriod(years)}
                          className="quick-select-btn success"
                        >
                          {years}Y
                        </button>
                      ))
                    : [6, 12, 24, 36].map((months) => (
                        <button
                          key={months}
                          onClick={() => setInvestmentPeriod(months)}
                          className="quick-select-btn success"
                        >
                          {months}M
                        </button>
                      ))
                  }
                </div>
              </div>

              {/* Period Type */}
              <div className="input-group">
                <label>Period Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "years", label: "Years" },
                    { value: "months", label: "Months" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setPeriodType(type.value);
                        setInvestmentPeriod(type.value === "years" ? 3 : 36);
                      }}
                      className={`quick-select-btn ${periodType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dividends/Income Received */}
              <div className="input-group">
                <label>Dividends/Income Received</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={dividendsReceived}
                    onChange={(e) => setDividendsReceived(e.target.value)}
                    className="calc-input"
                    placeholder="5000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 5000, 10000, 25000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setDividendsReceived(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateROI} className="calculate-btn">
                Calculate ROI
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Return Analysis
              </h2>

              <div className="result-card">
                {/* ROI Percentage - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Return on Investment</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.roiPercentage}%
                  </div>
                  <div className="result-subtext">
                    {result.investmentCategory} Performance
                  </div>
                </div>

                {/* Net Profit */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Net Profit/Loss</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netProfit).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {parseFloat(result.netProfit) >= 0 ? "Profit earned" : "Loss incurred"}
                  </div>
                </div>

                {/* Annualized Return */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Annualized Return (CAGR)</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                        <polyline points="17 6 23 6 23 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.annualizedReturn}%
                  </div>
                  <div className="result-subtext">
                    Per year average
                  </div>
                </div>

                {/* Monthly Return */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Average Monthly Gain</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.monthlyReturn).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Per month average
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Investment Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total Investment</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.totalInvestment) / parseFloat(result.totalReturn)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total Return</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.totalReturn).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Profit/Loss</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.netProfit).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className={`breakdown-bar-fill ${parseFloat(result.netProfit) >= 0 ? 'green' : 'blue'}`}
                        style={{
                          width: `${Math.abs((parseFloat(result.netProfit) / parseFloat(result.totalReturn)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Return on Investment (ROI)</h3>
              <p>
                Return on Investment (ROI) is a fundamental financial metric that measures the profitability of an 
                investment relative to its cost. It's expressed as a percentage and helps investors evaluate the 
                efficiency of different investments. A positive ROI indicates profit, while negative ROI shows a loss. 
                ROI considers the total returns including capital appreciation and income (dividends, interest, rental) 
                against the total investment made. While simple ROI gives overall returns, annualized ROI (CAGR) provides 
                a more accurate picture by accounting for the time value of money. This calculator helps you analyze past 
                investment performance or project future returns. Remember that historical ROI doesn't guarantee future 
                performance.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>ROI Calculation Formula:</h4>
            <div className="formula-display">
              ROI = [(Final Value + Income - Total Investment) ÷ Total Investment] × 100
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Total Investment</div>
                <div className="formula-param-value">Initial + additional investments</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Final Value</div>
                <div className="formula-param-value">Current market value of investment</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Income</div>
                <div className="formula-param-value">Dividends, interest, or rental income</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">CAGR</div>
                <div className="formula-param-value">Annualized growth rate over time</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is a good ROI percentage for investments?</h5>
              <p>
                A "good" ROI varies significantly by asset class and market conditions. In India, traditional fixed 
                deposits offer 5-7% annual returns with minimal risk. Debt mutual funds typically provide 7-9% returns. 
                Equity mutual funds historically average 12-15% annually over long periods, though with higher volatility. 
                Direct stock investments can yield 15-25%+ for skilled investors but carry substantial risk. Real estate 
                appreciation varies by location, typically 8-12% annually in growing cities. Gold averages 8-10% over 
                decades. Any ROI above inflation rate (4-6% in India) represents real wealth growth.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between ROI and CAGR?</h5>
              <p>
                ROI (Return on Investment) is the total percentage return over the entire investment period without 
                considering time. If you invest ₹1,00,000 and it becomes ₹1,50,000, your ROI is 50% regardless of 
                whether this took 2 years or 10 years. CAGR (Compound Annual Growth Rate) is the annualized rate of 
                return assuming profits are reinvested. It smooths out volatility and shows the steady annual growth 
                rate needed to reach the final value. For the same investment: if 50% ROI occurred over 3 years, 
                CAGR is 14.47% (not 50%/3=16.67%).
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I include taxes and fees in ROI calculation?</h5>
              <p>
                Yes, for accurate ROI assessment, include all costs and taxes. Investment costs include brokerage fees, 
                transaction charges, mutual fund expense ratios, demat account charges, advisory fees, and property 
                transaction costs. Tax implications significantly impact actual returns: short-term capital gains (STCG) 
                on equity at 20%, long-term capital gains (LTCG) above ₹1.25 lakh at 12.5%, debt funds taxed as per 
                income slab, dividend distribution tax where applicable, and TDS on various instruments. For example, 
                if gross ROI is 20% but taxes and fees consume 5%, actual net ROI is approximately 14-15%. Real estate 
                ROI should factor stamp duty, registration, maintenance, property tax, and selling costs.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate ROI for SIP investments?</h5>
              <p>
                Systematic Investment Plan (SIP) ROI calculation is complex because investments happen over time at 
                different market levels, unlike lump sum investments. Simple ROI formula doesn't work accurately for 
                SIPs. Instead, use XIRR (Extended Internal Rate of Return) which accounts for multiple cash flows at 
                different dates. Most mutual fund platforms show XIRR for your SIP investments. Alternatively, use the 
                absolute return method: compare total invested amount with current value, then annualize it. For manual 
                calculation, track each SIP installment with its purchase date and units, calculate current value, and 
                find weighted average returns.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can ROI be negative, and what does it mean?</h5>
              <p>
                Yes, negative ROI indicates investment loss where current value plus any income received is less than 
                total investment. For example, investing ₹1,00,000 now worth ₹80,000 shows -20% ROI. Negative returns 
                occur due to market downturns, poor investment selection, business failures, or adverse economic 
                conditions. In equity markets, short-term negative ROI is common and expected; historically, markets 
                recover over longer periods (5-10+ years). However, negative ROI in safer instruments like fixed deposits 
                is extremely rare. When facing negative ROI, avoid panic selling in equity investments unless fundamentals 
                have permanently deteriorated.
              </p>
            </div>

            <div className="faq-item">
              <h5>How does inflation affect my investment ROI?</h5>
              <p>
                Inflation significantly impacts real investment returns by reducing purchasing power. Nominal ROI shows 
                actual percentage gains, while real ROI adjusts for inflation to show true wealth increase. Calculate 
                real ROI as: Real ROI ≈ Nominal ROI - Inflation Rate. If investment returns 10% but inflation is 6%, 
                real ROI is approximately 4% - your actual wealth growth. In India, inflation averages 4-6% annually 
                (varying by period). Any investment returning less than inflation causes real wealth erosion despite 
                positive nominal returns. For example, a fixed deposit at 6% with 6% inflation provides 0% real return - 
                you maintain purchasing power but don't grow wealth.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ROICalculator;